import config from 'garaje/config/environment';
import { createURN } from 'garaje/models/generic-resource';
import { type ReservationLogQueryFilter } from 'garaje/pods/resources/reservations/controller';
import type { ResourcesQueryFilter } from 'garaje/types/generic-resources/QueryFilter';
import { type WorkbookScopeType } from 'garaje/utils/workbooks';

/**
 *
 * @param {string} path       API path
 * @param {string} baseUrl    base URL for the API
 * @returns {string} url
 */

export interface ResourceReservationQueryFilter {
  'location-id'?: string;
  'start-time'?: string;
  'end-time'?: string;
  'resource-type-id'?: string;
  'employee-id'?: string;
}
export interface ResourceAvailabilityQueryFilter {
  'location-id'?: string;
  'start-time'?: string;
  'end-time'?: string;
  'resource-type-ids'?: string;
  'employee-id'?: string;
}

interface FetchEmployeeInviteOnDateFilter {
  locationId: string | undefined;
  dateTo: string;
  dateFrom: string;
  email: string;
}

export function buildUrl(path: string, baseUrl = config.apiHost): string {
  if (path.charAt(0) !== '/') {
    baseUrl = `${baseUrl}/`;
  }

  return `${baseUrl}${path}`;
}

export default {
  spacesAIApiUrl(): string {
    return buildUrl(`/predict`, config.spacesAIHost);
  },
  bulkDesksUpdate(): string {
    return buildUrl(`/a/rms/desks/bulk-desks-update`, config.rmsHost);
  },
  photoUrl(photoPath: string): string {
    return buildUrl(photoPath);
  },
  analyticsUrl(path: string): string {
    return buildUrl(`/a/visitors/api/v2/analytics/${path}`, config.apiHost);
  },
  envoyLogoutUrl(): string {
    return buildUrl(`/users/sign_out`, config.apiHost);
  },
  pluginOAuth2Url(key: string, app: string, locationId: string): string {
    return buildUrl(`/platform/${key}/oauth2/${app}/connect?state=popup&location_id=${locationId}`, config.apiHost);
  },
  pluginCallbackAuthUrl(key: string, route: string, locationId: string): string {
    return buildUrl(`/platform/${key}/${route}?location_id=${locationId}`, config.apiHost);
  },
  salesforceAuthUrl(): string {
    return buildUrl('/users/auth/salesforce?display=popup', config.envoyHost);
  },
  scimBaseUrl(): string {
    return buildUrl('/scim/v2', config.apiHost);
  },
  samlConsumeUrl(): string {
    return buildUrl('/a/saml/consume', config.apiHost);
  },
  samlMetadataUrl(): string {
    return buildUrl('/a/saml/metadata', config.apiHost);
  },
  samlInitUrl(companyId: string): string {
    return buildUrl(`/a/saml/auth/${companyId}`, config.apiHost);
  },
  googleAuthUrl(): string {
    return buildUrl('/users/auth/google_oauth2?state=popup', config.envoyHost);
  },
  appStoreUrl(): string {
    return buildUrl('/apps', config.host);
  },
  billingDashboardUrl(): string {
    return buildUrl('/billing', config.host);
  },
  pluginStatusesUrl(): string {
    return buildUrl('/a/badge-log/v1/plugin-install-statuses', config.badgeLogApiHost);
  },
  locationNotificationSettingsUrl(locationId: string): string {
    return buildUrl(`/a/badge-log/v1/locations/${locationId}/notification-settings`, config.badgeLogApiHost);
  },
  presenceSettingsUrl(locationId: string): string {
    return buildUrl(`/a/presence-service/v1/locations/${locationId}/presence-settings`, config.presenceApiHost);
  },
  signupReferralUrl(referralCode: string): string {
    return buildUrl(`/sign_up?ref=${encodeURIComponent(referralCode)}`, config.envoyHost);
  },
  embeddedRoomsLocationAnalyticsUrl(companyId: string): string {
    return buildUrl(`/dashboards/rooms_location_analytics?company_id=${companyId}`, config.customerDashHost);
  },
  embeddedDeliveriesAnalyticsUrl(companyId: string, locationId: string, deliveryAreaId: string): string {
    let url = `/dashboards/deliveries_analytics?company_id=${companyId}&location_id=${locationId}`;
    if (deliveryAreaId) {
      url += `&delivery_area_id=${deliveryAreaId}`;
    }
    return buildUrl(url, config.customerDashHost);
  },
  embeddedProtectLocationAnalyticsUrl(companyId: string, locationId: string): string {
    return buildUrl(
      `/dashboards/protect_location_analytics?company_id=${companyId}&location_id=${locationId}`,
      config.customerDashHost,
    );
  },
  embeddedVisitorsLocationAnalyticsUrl(companyId: string, locationId: string): string {
    return buildUrl(
      `/dashboards/visitors_location_analytics?company_id=${companyId}&location_id=${locationId}`,
      config.customerDashHost,
    );
  },
  embeddedReportListGlobalAnalyticsUrl(companyId: string, locationId: string): string {
    return buildUrl(`/report/list?company_id=${companyId}&location_id=${locationId}`, config.customerDashHost);
  },
  embeddedScheduleReportsModalUrl(
    companyId: string,
    locationId: string,
    flowId: string,
    start: string,
    end: string,
  ): string {
    return buildUrl(
      `/modals/report/invites?company_id=${companyId}&location_id=${locationId}&start=${start}&end=${end}&flow_id=${flowId}`,
      config.customerDashHost,
    );
  },
  embeddedEditScheduleReportsModalUrl(companyId: string, locationId: string, modalId: string): string {
    return buildUrl(
      `/modals/report/edit/${modalId}?company_id=${companyId}&location_id=${locationId}`,
      config.customerDashHost,
    );
  },
  embeddedDeleteScheduleReportsModalUrl(reportId: string, companyId: string): string {
    return buildUrl(`modals/report/delete/${reportId}?company_id=${companyId}`, config.customerDashHost);
  },
  sigmaScheduleReportsModalUrlLocation(companyId: string, locationId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(`/reports/v2/modal?company_id=${companyId}&location_id=${locationId}`);
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  sigmaScheduleReportsModalUrlGlobal(companyId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(`/reports/v2/modal?company_id=${companyId}`);
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedEntryExportModalUrl(
    companyId: string,
    locationId: string,
    flowId: string | null,
    start: string,
    end: string,
    label: string,
  ): string {
    return buildUrl(
      `/modals/export/entries?company_id=${companyId}&location_id=${locationId}&start=${start}&end=${end}&flow_id=${flowId}&label=${label}`,
      config.customerDashHost,
    );
  },
  embeddedInviteExportModalUrl(
    companyId: string,
    locationId: string,
    flowId: string | null | undefined,
    start: string,
    end: string,
    label: string,
  ): string {
    return buildUrl(
      `/modals/export/invites?company_id=${companyId}&location_id=${locationId}&start=${start}&end=${end}&flow_id=${flowId}&label=${label}`,
      config.customerDashHost,
    );
  },
  embeddedVisitorsGlobalAnalyticsUrl(companyId: string): string {
    return buildUrl(`/dashboards/visitors_global_analytics?company_id=${companyId}`, config.customerDashHost);
  },
  embeddedDesksLocationAnalyticsUrl(companyId: string, locationId: string): string {
    return buildUrl(
      `/dashboards/desks_location_analytics?company_id=${companyId}&location_id=${locationId}`,
      config.customerDashHost,
    );
  },
  embeddedProtectGlobalAnalyticsUrl(companyId: string): string {
    return buildUrl(`/dashboards/protect_global_analytics?company_id=${companyId}`, config.customerDashHost);
  },
  embeddedMigratedProtectGlobalAnalyticsUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = `/protect/global/analytics?company_id=${companyId}`;
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedProtectGlobalReportsUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = `/protect/global/reports?company_id=${companyId}`;
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedProtectGlobalReportsModalUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(`/protect/global/modal?company_id=${companyId}`);
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedDesksGlobalAnalyticsUrl(companyId: string): string {
    return buildUrl(`/dashboards/desks_global_analytics?company_id=${companyId}`, config.customerDashHost);
  },
  embeddedConnectAnalyticsUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = `/connect/analytics?company_id=${companyId}`;
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedConnectOccupancyAnalyticsDemoUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = `/connect/occupancy?company_id=${companyId}`;
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedConnectOccupancyAnalyticsUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = `/connect/occupancy-analytics?company_id=${companyId}`;
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedOccupancyLocationAnalyticsUrl(companyId: string, locationId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(
      `/occupancy/location/analytics?company_id=${companyId}&location_id=${locationId}`,
    );
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedOccupancyLocationReportsModalUrl(companyId: string, locationId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(
      `/occupancy/location/modal?company_id=${companyId}&location_id=${locationId}`,
    );
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedWorkplaceActiveUsersModalUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(`/workplace/billing?company_id=${companyId}`);
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedOccupancyGlobalAnalyticsUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(`/occupancy/global/analytics?company_id=${companyId}`);
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedSettingsGlobalAnalyticsUrl(companyId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(`/settings/global/analytics?company_id=${companyId}`);
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedOccupancyCsvUploadDeleteModal(companyId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(`/occupancy/csv-upload/delete-modal?company_id=${companyId}`);
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedOccupancyCsvUploadResultsModal(companyId: string, loginRequired: boolean): string {
    const redirectUrl = encodeURIComponent(`/occupancy/csv-upload/results-modal?company_id=${companyId}`);
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedRoombaLocationAnalyticsMigratedUrl(
    companyId = '',
    locationId = '',
    oauthProvider = '',
    loginRequired = true,
  ): string {
    const redirectUrl = encodeURIComponent(
      `/rooms/location/analytics?company_id=${companyId}&location_id=${locationId}&oauth_provider=${oauthProvider}`,
    );
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedOccupancyCopyLinkModal(url: string, loginRequired: boolean, companyId: string, locationId: string): string {
    const redirectUrl = encodeURIComponent(
      `/occupancy/location/copy-link-modal-page?company_id=${companyId}&${
        locationId ? `location_id=${locationId}&` : ''
      }url=${url}`,
    );
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedOccupancyLocationReportListUrl(loginRequired: boolean, companyId: string, locationId: string): string {
    const redirectUrl = encodeURIComponent(
      `/occupancy/location/reports?company_id=${companyId}&location_id=${locationId}`,
    );
    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedAttendanceAnalyticsUrl(loginRequired: boolean, companyId: string, locationId: string, view: string): string {
    const redirectUrl = encodeURIComponent(
      `/attendance/location/analytics?company_id=${companyId}&location_id=${locationId}&view=${view}`,
    );

    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedAttendanceGlobalAnalyticsUrl(loginRequired: boolean, companyId: string, view: string): string {
    const redirectUrl = encodeURIComponent(`/attendance/global/analytics?company_id=${companyId}&view=${view}`);

    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedAttendanceAnalyticsV2Url(
    loginRequired: boolean,
    companyId: string,
    locationId: string,
    view: string,
  ): string {
    const redirectUrl = encodeURIComponent(
      `/attendance/location/analytics-v2?company_id=${companyId}&location_id=${locationId}&view${view}`,
    );

    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  embeddedAttendanceGlobalAnalyticsV2Url(loginRequired: boolean, companyId: string, view: string): string {
    const redirectUrl = encodeURIComponent(`/attendance/global/analytics-v2?company_id=${companyId}&view=${view}`);

    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  scheduledReportsV2LocationUrl(loginRequired: boolean, companyId: string, locationId: string): string {
    const redirectUrl = encodeURIComponent(`/reports/v2/location?company_id=${companyId}&location_id=${locationId}`);

    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  scheduledReportsV2GlobalUrl(loginRequired: boolean, companyId: string): string {
    const redirectUrl = encodeURIComponent(`/reports/v2/global?company_id=${companyId}`);

    return buildUrl(
      `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
      config.workplaceAnalyticsHost,
    );
  },
  v1: {
    logoUploadUrl(id: string): string {
      return buildUrl(`/api/v1/locations/${id}`);
    },
    printBadgesUrl(locationId: string): string {
      return buildUrl(`/api/v1/locations/${locationId}/print_badges`);
    },
    welcomeImageUploadUrl(id: string): string {
      return buildUrl(`/api/v1/configs/${id}`);
    },
    generatePreRegistrationLinkUrl(id: string): string {
      return buildUrl(`/api/v1/locations/${id}/generate_pre_registration_link`);
    },
    generateSecurityDeskLinkUrl(id: string): string {
      return buildUrl(`/api/v1/locations/${id}/generate_security_desk_link`);
    },
  },
  v2: {
    visitorsLandingPage(locationId: string): string {
      return buildUrl(`/a/visitors/api/v2/analytics/visitors-landing-page/${locationId}`);
    },
    deliveriesLandingPage(locationId: string): string {
      return buildUrl(`/a/deliveries/api/v2/analytics/deliveries-landing-page/${locationId}`, config.deliveriesHost);
    },
    roomsActiveCountUrl(companyId: string): string {
      return buildUrl(`/a/roomba/billing/status?company_id=${companyId}`, config.roomsHost);
    },
    roomsLandingPage(locationId: string): string {
      return buildUrl(`/a/roomba/analytics/rooms-landing-page/${locationId}`, config.roomsHost);
    },
    roomsOnboardingStatus(locationId: string): string {
      return buildUrl(`/a/roomba/onboarding_status?location_id=${locationId}`, config.roomsHost);
    },
    roomsContactTracingCsv(emails: string[], startDate: string, endDate: string): string {
      const apiBase = 'a/rooms-v2';
      const emailList: string[] = [];
      emails.forEach((email) => {
        emailList.push(encodeURIComponent(email));
      });
      const encodedEmails = emailList.join(',');

      return buildUrl(
        `${apiBase}/contact_tracing?emails=${encodedEmails}&start_time=${startDate}&end_time=${endDate}&location_based=true`,
        config.roomsHost,
      );
    },
    roomsCalendarPermissionsCheck(companyRoomId: string): string {
      return buildUrl(`/a/rms/api/v2/calendar_permissions/${companyRoomId}`);
    },
    entryBulkUrl(): string {
      return buildUrl('/a/visitors/api/v2/entries/bulk-create');
    },
    pubnubChannels(): string {
      return buildUrl('/a/visitors/api/v2/me/pubnub-channels');
    },
    entriesCalendarUrl(locationId: string, date: string): string {
      return buildUrl(`/a/visitors/api/v2/locations/${locationId}/calendar?filter%5Bdate%5D=${date}`);
    },
    emailNotificationPreviewUrl(locationId: string): string {
      return buildUrl(`/a/visitors/api/v2/locations/${locationId}/notification-preview`);
    },
    generateSecurityDeskLinkUrl(locationId: string): string {
      return buildUrl(`/a/visitors/api/v2/locations/${locationId}/security-desk-link`);
    },
    invitePreviewUrl(locationId: string): string {
      return buildUrl(`/a/visitors/api/v2/locations/${locationId}/invite-preview`);
    },
    sendInvitePreviewUrl(locationId: string): string {
      return buildUrl(`/a/visitors/api/v2/locations/${locationId}/send-invite-preview`);
    },
    generateApiKeyUrl(id: string): string {
      return buildUrl(`/a/visitors/api/v2/locations/${id}/generate-key`);
    },
    updateLocationPhotoUrl(locationId: string): string {
      return buildUrl(`/a/visitors/api/v2/locations/${locationId}/logo`);
    },
    slideshowUploadUrl(): string {
      return buildUrl('/a/visitors/api/v2/slideshows');
    },
    integrations: {
      enabled(): string {
        return buildUrl('/a/visitors/api/v2/integrations/enabled');
      },
      googleAppsUrl(id = ''): string {
        return buildUrl(`/a/visitors/api/v2/integrations/google-apps/${id}`);
      },
      googleAppUsersUrl(id: string): string {
        return buildUrl(`/a/visitors/api/v2/integrations/google-apps/${id}/users`);
      },
      zapierIntegrationsUrl(id = ''): string {
        return buildUrl(`/a/visitors/api/v2/integrations/zapier-integrations/${id}`);
      },
      iftttIntegrationsUrl(id = ''): string {
        return buildUrl(`/a/visitors/api/v2/integrations/ifttt-integrations/${id}`);
      },
      oneLoginUsersUrl(id: string): string {
        return buildUrl(`/a/visitors/api/v2/integrations/one-logins/${id}/users`);
      },
      oneLoginCustomAttributesUrl(id: string): string {
        return buildUrl(`/a/visitors/api/v2/integrations/one-logins/${id}/custom-attributes`);
      },
      oktaUsersUrl(id: string): string {
        return buildUrl(`/a/visitors/api/v2/integrations/okta/${id}/users`);
      },
      oktaCustomAttributesUrl(id: string): string {
        return buildUrl(`/a/visitors/api/v2/integrations/okta/${id}/custom-attributes`);
      },
      oktaGroupsUrl(id: string): string {
        return buildUrl(`/a/visitors/api/v2/integrations/okta/${id}/groups`);
      },
      salesforceIntegrationsUrl(id = ''): string {
        return buildUrl(`/a/visitors/api/v2/integrations/salesforce-integrations/${id}`);
      },
    },
    users: {
      search(): string {
        return buildUrl(`/a/visitors/api/v2/users/lookup`, config.apiOverwrittenHost);
      },
    },
    userRoles(): string {
      return buildUrl('/a/visitors/api/v2/user-roles');
    },
    bulkCreateRoles(): string {
      return buildUrl('/a/visitors/api/v2/role-assignments/bulk');
    },
    bulkDeleteRoles(): string {
      return buildUrl('/a/visitors/api/v2/role-assignments/bulk-destroy');
    },
    assignmentCountsPerRole(): string {
      return buildUrl('/a/visitors/api/v2/role-assignments/count');
    },
    distinctMappingValues(scimPrimaryLocationConfigId: string): string {
      return buildUrl(
        `/a/visitors/api/v2/scim-primary-location-configurations/${scimPrimaryLocationConfigId}/distinct-mapping-values`,
      );
    },
    config: {
      attachmentsUrl(configId: string, type: string): string {
        return buildUrl(`/a/visitors/api/v2/configs/${configId}/attachments/${type}`);
      },
    },
  },
  v3: {
    blocklistFilter: {
      photoUpload(id: string): string {
        return buildUrl(`/a/visitors/api/v3/blacklist-filters/${id}/attachments/photo`);
      },
      uploadCsv(locationId: string, deleteAllFirst: boolean): string {
        return buildUrl(
          `/a/visitors/api/v3/blacklist-filters/upload?location_id=${locationId}&delete_all_first=${deleteAllFirst}`,
        );
      },
      uploadCsvStatus(): string {
        return buildUrl(`/a/visitors/api/v3/blacklist-filters/upload-status`);
      },
    },
    csvUpload(): string {
      return buildUrl('/a/visitors/api/v3/csv-uploads');
    },
    employees: {
      synchronizeUrl(companyId: string): string {
        return buildUrl(`/a/visitors/api/v3/employees/synchronize?company_id=${companyId}`);
      },
      inviteUser(id: string): string {
        return buildUrl(`/a/visitors/api/v3/employees/${id}/invite`);
      },
      uploadUrl(): string {
        return buildUrl('/a/visitors/api/v3/employees/upload');
      },
      uploadStatusUrl(): string {
        return buildUrl('/a/visitors/api/v3/employees/upload-status');
      },
      exportUrl(companyId: string): string {
        return buildUrl(`/a/visitors/api/v3/employees/export?company_id=${companyId}`);
      },
      bulkUrl(): string {
        return buildUrl('/a/visitors/api/v3/employees/bulk_create');
      },
    },
    entry: {
      previousEntriesUrl(id: string): string {
        return buildUrl(`/a/visitors/api/v3/entries/${id}/previous-entries`);
      },
    },
    groupInvites: {
      sendNotification(id: string): string {
        return buildUrl(`/a/visitors/api/v3/group-invites/${id}/notify`);
      },
    },
    invites: {
      bulkUrl(): string {
        return buildUrl('/a/visitors/api/v3/invites/bulk');
      },
      sendNotification(id: string): string {
        return buildUrl(`/a/visitors/api/v3/invites/${id}/notify`);
      },
      fetchEmployeeInviteOnDate(filter: FetchEmployeeInviteOnDateFilter): string {
        return buildUrl(
          `/a/visitors/api/v3/invites?filter[location]=${filter.locationId}&filter[email]=${filter.email}&filter[employee-centric]=true&filter[datetime-from]=${filter.dateFrom}&filter[datetime-to]=${filter.dateTo}`,
        );
      },
    },
    recurringInvites: {
      sendNotification(id: string): string {
        return buildUrl(`/a/visitors/api/v3/recurring-invites/${id}/notify`);
      },
    },
    salesforceLeads: {
      createUrl(): string {
        return buildUrl('/a/visitors/api/v3/salesforce-leads');
      },
    },
    scimDataAutocomplete(term: string, limit: number): string {
      return buildUrl(`/a/visitors/api/v3/scim-data?filter[name]=${term}&filter[limit]=${limit}`);
    },
    placesAutocomplete(term: string, types?: string[]): string {
      const typesFilter = types?.map((type) => (type ? `&filter[types][]=${type}` : '')).join('') ?? '';

      return buildUrl(`/a/visitors/api/v3/places/autocomplete?filter[search]=${term}${typesFilter}`);
    },
    summaryPageImageUpload(id: string): string {
      return buildUrl(`/a/visitors/api/v3/summary-pages/${id}/attachments/image`);
    },
    sendTestNotificationUrl(): string {
      return buildUrl('/a/visitors/api/v3/sign-in-field-action-rule-groups/send-test-notification');
    },
    userDocuments: {
      approveUserDocumentLink(id: string): string {
        return buildUrl(`/a/visitors/api/v3/user-document-links/${id}/approve`);
      },
      denyUserDocumentLink(id: string): string {
        return buildUrl(`/a/visitors/api/v3/user-document-links/${id}/deny`);
      },
      resetUserDocumentLink(id: string): string {
        return buildUrl(`/a/visitors/api/v3/user-document-links/${id}/review`);
      },
    },
    users: {
      acceptPrivacyPolicy(): string {
        return buildUrl(`/a/visitors/api/v3/users/accept_privacy_policy`);
      },
    },
    printers: {
      badgePrintJobs(): string {
        return buildUrl(`/a/visitors/api/v3/badge-print-jobs`);
      },
    },
    getCompanyConfigurationUrl(companyId: string): string {
      return buildUrl(`/a/visitors/api/v3/companies/${companyId}/company-configuration`);
    },
    diplomatClients: {
      hash(os: string, arch: string): string {
        return buildUrl(`/a/visitors/api/v3/diplomat-clients/hash?os=${os}&arch=${arch}`);
      },
      download(os: string, arch: string): string {
        return buildUrl(`/a/visitors/api/v3/diplomat-clients/download?os=${os}&arch=${arch}`);
      },
      publicKey(): string {
        return buildUrl('/a/visitors/api/v3/diplomat-clients/publickey');
      },
      hashSignature(os: string, arch: string): string {
        return buildUrl(`/a/visitors/api/v3/diplomat-clients/signature?os=${os}&arch=${arch}`);
      },
    },
    visitorProfiles: {
      visitStats(profileId: string): string {
        return buildUrl(`/a/visitors/api/v3/visitor-profiles/${profileId}/visit-stats`);
      },
    },
  },
  hooks: {
    v1: {
      triggerZoneUiHooksUrl(): string {
        return buildUrl(`/a/hooks/v1/zones/hooks/ui/trigger`, config.hooksHost);
      },
    },
  },
  roomba: {
    getAvailableRooms(locationId: string): string {
      return buildUrl(
        `/a/roomba/json-api/rooms?filter[location]=${locationId}&filter[available_only]=false`,
        config.apiHost,
      );
    },
    googleAuthUrl(): string {
      return buildUrl(`/a/roomba/auth/google`, config.roomsHost);
    },
    microsoftAuthUrl(): string {
      return buildUrl(`/a/roomba/auth/microsoft/service`, config.roomsHost);
    },
    microsoftOfficeUrl(): string {
      return buildUrl(`/a/roomba/auth/microsoft/user`, config.roomsHost);
    },
  },
  rms: {
    updateCaptainsUrl(neighborhoodId: string): string {
      const baseUrl = `/a/rms/v2/generics/neighborhoods/${neighborhoodId}/captains`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    bulkDesksUpdateEndpoint(includeAssignments: boolean = false): string {
      const baseUrl = includeAssignments
        ? `/a/rms/v2/generics/resources/bulk-update?include=assignments`
        : `/a/rms/v2/generics/resources/bulk-update`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    bulkDesksCreateEndpoint(includeAssignments: boolean = false): string {
      const baseUrl = includeAssignments
        ? `/a/rms/v2/generics/resources/bulk-create?include=assignments`
        : `/a/rms/v2/generics/resources/bulk-create`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    bulkDesksDeleteEndpoint(): string {
      return buildUrl(`/a/rms/v2/generics/resources/bulk-delete`, config.rmsHost);
    },
    googleAuthUrl(companyId: string = ''): string {
      return buildUrl(`/a/rms/auth/google?company_id=${companyId}`, config.rmsHost);
    },
    microsoftAuthUrl(companyId: string = ''): string {
      return buildUrl(`/a/rms/auth/microsoft?company_id=${companyId}`, config.rmsHost);
    },
    microsoftOfficeUrl(companyId: string = ''): string {
      return buildUrl(`/a/rms/auth/microsoft?company_id=${companyId}`, config.rmsHost);
    },
    floorplanUrl(floorId: string): string {
      return buildUrl(`/a/rms/floors/${floorId}/floor-plan`, config.rmsHost);
    },
    uploadDesksCsv(locationId: string, draftId: string | null = null): string {
      if (draftId) {
        return buildUrl(
          `/a/rms/desks/upload-desks-per-location?location_id=${locationId}&draft_id=${draftId}`,
          config.rmsHost,
        );
      }
      return buildUrl(`/a/rms/desks/upload-desks-per-location?location_id=${locationId}`, config.rmsHost);
    },
    downloadDesksCsv(locationId: string, draftId: string | null = null): string {
      if (draftId) {
        return buildUrl(
          `/a/rms/desks/download-desks?filter[location-id]=${locationId}&filter[draft-id]=${draftId}`,
          config.rmsHost,
        );
      }
      return buildUrl(`/a/rms/desks/download-desks?filter[location-id]=${locationId}`, config.rmsHost);
    },
    uploadResourcesCsv(locationId: string, resourceTypeId: string): string {
      const relationId = createURN('location', locationId);
      const baseUrl = `/a/rms/v2/generics/resources/csv/upload?resource-type-id=${resourceTypeId}&relation-id=${relationId}`;

      return buildUrl(baseUrl, config.rmsHost);
    },
    downloadResourcesCsv(locationId: string, resourceTypeId: string, isTemplate: boolean = false): string {
      const relationId = createURN('location', locationId);
      const baseUrl = `/a/rms/v2/generics/resources/csv/download?resource-type-id=${resourceTypeId}&relation-id=${relationId}`;

      return isTemplate ? `/a/rms/v2/generics/resources/csv/download/template` : buildUrl(baseUrl, config.rmsHost);
    },
    getDesksInNeighborhood(neighborhoodId: string, locationId: string): string {
      return buildUrl(
        `a/rms/desks?filter[location-id]=${locationId}&filter[neighborhood-id]=${neighborhoodId}`,
        config.rmsHost,
      );
    },
    reservation(entryId: string): string {
      return buildUrl(`/a/rms/reservations?filter[entry-id]=${entryId}&include=desk`, config.rmsHost);
    },
    bulkCreateReservations(): string {
      return buildUrl(`/a/rms/bulk-reservations`, config.rmsHost);
    },
    getDeskAvailability(): string {
      return buildUrl(`/a/rms/available-desks`, config.rmsHost);
    },
    getResourceAvailability(filters: ResourceAvailabilityQueryFilter): string {
      const queryParams: string[] = [];

      if (filters['location-id']) {
        queryParams.push(`location-id=${filters['location-id']}`);
      }

      if (filters['start-time']) {
        queryParams.push(`start-time=${filters['start-time']}`);
      }

      if (filters['end-time']) {
        queryParams.push(`end-time=${filters['end-time']}`);
      }

      if (filters['resource-type-ids']) {
        queryParams.push(`resource-type-ids=${filters['resource-type-ids']}`);
      }

      if (filters['employee-id']) {
        queryParams.push(`employee-id=${filters['employee-id']}`);
      }

      return buildUrl(`/a/rms/v2/generics/resources/availability/search?${queryParams.join('&')}`, config.rmsHost);
    },
    sendAnnouncementNotification(announcementId: string): string {
      return buildUrl(`/a/rms/announcements/${announcementId}`, config.rmsHost);
    },
    releaseDesk(): string {
      return buildUrl(`/a/rms/desks/abstract/release-desk`, config.rmsHost);
    },
    bulkEntryCheckIn(): string {
      return buildUrl(`/a/rms/desks/abstract/bulk-entry-check-in`, config.rmsHost);
    },
    bulkResourcesUpdate(): string {
      const baseUrl = '/a/rms/v2/generics/resources/bulk-update';
      return buildUrl(baseUrl, config.rmsHost);
    },
    resourceTypes(): string {
      const baseUrl = `/a/rms/v2/generics/resource-types`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    resourceType(resourceTypeId: number, locationId: string): string {
      const baseUrl = `/a/rms/v2/generics/resource-types/${resourceTypeId}?location-id=${locationId}`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    amenities(resourceTypeId: number, locationId?: string): string {
      const baseUrl = `/a/rms/v2/generics/resource-types/${resourceTypeId}/amenities${
        locationId ? `?location-id=${locationId}` : ''
      }`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    amenity(resourceTypeId: number, amenityId: string): string {
      const baseUrl = `/a/rms/v2/generics/resource-types/${resourceTypeId}/amenities/${amenityId}`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    deskAmenity(deskId: number): string {
      const baseUrl = `/a/rms/amenities/${deskId}`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    resourceReservations(): string {
      const baseUrl = '/a/rms/v2/generics/reservations';
      return buildUrl(baseUrl, config.rmsHost);
    },
    deleteResourceReservation(reservationId: number): string {
      const baseUrl = `/a/rms/v2/generics/reservations/${reservationId}`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    changeResourceReservation(reservationId: number): string {
      const baseUrl = `/a/rms/v2/generics/reservations/${reservationId}/change`;
      return buildUrl(baseUrl, config.rmsHost);
    },
    getResourceReservations(filters: ResourceReservationQueryFilter): string {
      const baseUrl = '/a/rms/v2/generics/reservations';
      const queryParams: string[] = [];

      if (filters['location-id']) {
        queryParams.push(`location-id=${filters['location-id']}`);
      }

      if (filters['start-time']) {
        queryParams.push(`start-time=${filters['start-time']}`);
      }

      if (filters['end-time']) {
        queryParams.push(`end-time=${filters['end-time']}`);
      }

      if (filters['resource-type-id']) {
        queryParams.push(`resource-type-id=${filters['resource-type-id']}`);
      }

      if (filters['employee-id']) {
        queryParams.push(`employee-id=${filters['employee-id']}`);
      }

      return buildUrl(`${baseUrl}?${queryParams.join('&')}`, config.rmsHost);
    },
    reservationLog(locationId: string, filters: ReservationLogQueryFilter): string {
      const baseUrl = '/a/rms/v2/generics/reservations/log';
      const queryParams: string[] = [`location-id=${locationId}`];

      if (filters['start-time']) {
        queryParams.push(`start-time=${filters['start-time']}`);
      }

      if (filters['end-time']) {
        queryParams.push(`end-time=${filters['end-time']}`);
      }

      if (filters['resource-type-id']) {
        queryParams.push(`resource-type-id=${filters['resource-type-id']}`);
      }

      if (filters['employee-id']) {
        queryParams.push(`employee-id=${filters['employee-id']}`);
      }

      if (filters['floor-id']) {
        queryParams.push(`floor-id=${filters['floor-id']}`);
      }

      if (filters['resource-id']) {
        queryParams.push(`resource-id=${filters['resource-id']}`);
      }

      if (filters['limit']) {
        queryParams.push(`limit=${filters['limit']}`);
      }

      if (filters['offset']) {
        queryParams.push(`offset=${filters['offset']}`);
      }

      return buildUrl(`${baseUrl}?${queryParams.join('&')}`, config.rmsHost);
    },
    resources(locationId: string, filters: ResourcesQueryFilter): string {
      const baseUrl = '/a/rms/v2/generics/resources';
      const queryParams: string[] = [`location-id=${locationId}`];

      if (filters['floor']) {
        queryParams.push(`floor=${filters['floor']}`);
      }

      if (filters['resource-type-id']) {
        queryParams.push(`resource-type-id=${filters['resource-type-id']}`);
      }

      if (filters['limit']) {
        queryParams.push(`limit=${filters['limit']}`);
      }

      if (filters['offset']) {
        queryParams.push(`offset=${filters['offset']}`);
      }

      return buildUrl(`${baseUrl}?${queryParams.join('&')}`, config.rmsHost);
    },
  },
  connect: {
    v1: {
      reorderFloors(propertyId: string): string {
        return buildUrl(`/a/multi-tenancy/api/v1/connect-properties/${propertyId}/reorder-floors`, config.apiHost);
      },
    },
  },
  multiTenancy: {
    /**
     *
     * @param {string} token  a token
     * @returns {string} url
     */
    claimToken(token: string): string {
      return buildUrl(`/a/multi-tenancy/api/v1/tenant-connection-tokens/${token}/claim`, config.apiHost);
    },
  },
  maps: {
    bulkFeaturesCreateEndpoint(): string {
      return buildUrl(`/a/maps/api/v2/map-features/bulk-create`, config.mapsHost);
    },
    bulkFeaturesUpdateEndpoint(): string {
      return buildUrl(`/a/maps/api/v1/map-features/bulk-update`, config.mapsHost);
    },
    bulkFeaturesDeleteEndpoint(): string {
      return buildUrl(`/a/maps/api/v2/map-features/bulk-delete`, config.mapsHost);
    },
    rasterImageUrl(mapFloorId: string): string {
      return buildUrl(`/a/maps/api/v1/map-floors/${mapFloorId}/raster-image`, config.mapsHost);
    },
    getLatestMapVersionEndpointByAreaMap(areaMapId: string): string {
      return buildUrl(`/a/maps/api/v1/map-versions/latest?filter[area-map]=${areaMapId}`, config.mapsHost);
    },
    getLiveMapUrl(filters: { locationId?: string; areaMapId?: string; startTime?: string; endTime?: string }): string {
      const baseUrl = '/a/maps/api/v1/live-map';
      const queryParams: string[] = [];

      // Endpoint accepts either a locationId or areaMapId
      if (filters.locationId) {
        queryParams.push(`filter[location]=${filters.locationId}`);
      } else if (filters.areaMapId) {
        queryParams.push(`filter[area-map]=${filters.areaMapId}`);
      }

      if (filters.startTime) {
        queryParams.push(`filter[start]=${filters.startTime}`);
      }

      if (filters.endTime) {
        queryParams.push(`filter[end]=${filters.endTime}`);
      }

      return buildUrl(`${baseUrl}?${queryParams.join('&')}`, config.mapsHost);
    },
    getMapSharingUrl(areaMapId: string): string {
      return buildUrl(`a/maps/api/v1/area-maps/${areaMapId}/share`, config.mapsHost);
    },
    deleteMapSharingUrl(shareId: string): string {
      return buildUrl(`a/maps/api/v1/shared-map/${shareId}`, config.mapsHost);
    },
    getDraftMapSharingUrl(draftMapId: string): string {
      return buildUrl(`a/maps/api/v1/drafts/${draftMapId}/share`, config.mapsHost);
    },
    getLiveMapV2Url(
      filters: {
        floorId?: string;
        locationId?: string;
        areaMapId?: string;
        startTime?: string;
        endTime?: string;
      },
      shareToken?: string,
    ): string {
      // TODO: shared map endpoint is v1 now but will migrage to v2 in the future
      // shareToken is URI encoded from backend
      const baseUrl = shareToken ? `/a/maps/api/v1/shared-map/external/${shareToken}` : '/a/maps/api/v2/live-map';
      const queryParams: string[] = [];

      // Endpoint accepts either a locationId or areaMapId
      if (filters.locationId) {
        queryParams.push(`filter[location]=${filters.locationId}`);
      } else if (filters.areaMapId) {
        queryParams.push(`filter[area-map]=${filters.areaMapId}`);
      }

      if (filters.floorId) {
        queryParams.push(`filter[floor]=${filters.floorId}`);
      }

      if (filters.startTime) {
        queryParams.push(`filter[start]=${filters.startTime}`);
      }

      if (filters.endTime) {
        queryParams.push(`filter[end]=${filters.endTime}`);
      }

      return buildUrl(`${baseUrl}?${queryParams.join('&')}`, config.mapsHost);
    },
  },
  growth: {
    getGrowthServiceUrl(): string {
      return buildUrl('/a/growth/api/v1/', config.growthApiHost);
    },
    getThinkificSsoUrl(): string {
      return buildUrl('/a/growth/api/v1/thinkific/sso', config.growthApiHost);
    },
  },
  authn: {
    authorize(): string {
      return buildUrl(`/a/auth/v0/authorize`, config.authnHost);
    },
    token(): string {
      return buildUrl(`/a/auth/v0/token`, config.authnHost);
    },
    mfaChallenge(): string {
      return buildUrl(`/a/mfa/challenge`, config.authnHost);
    },
  },
  authz: {
    getPermissionsUrl(companyId: string): string {
      return buildUrl(`/a/authz/api/v1/c/${companyId}/ui-permissions`, config.authzHost);
    },
    getPermissionSetsUrl(): string {
      return buildUrl(`/a/authz/api/v1/permission-sets`, config.authzHost);
    },
    getMatrixUrl(
      companyId: string,
      roleId: string,
      subscription: { hasVisitorsAccess?: boolean; hasWorkplaceAccess?: boolean; hasEmNoAccess?: boolean } = {},
    ): string {
      const { hasVisitorsAccess = false, hasWorkplaceAccess = false, hasEmNoAccess = false } = subscription;
      return buildUrl(
        `/a/authz/api/v1/c/${companyId}/roles/${roleId}/matrix?hasVisitorsAccess=${hasVisitorsAccess}&hasWorkplaceAccess=${hasWorkplaceAccess}&hasEmNoAccess=${hasEmNoAccess}`,
        config.authzHost,
      );
    },
    getRolesUrl(companyId: string): string {
      return buildUrl(`/a/authz/api/v1/c/${companyId}/roles`, config.authzHost);
    },
    getRoleUrl(companyId: string, roleId: string): string {
      return buildUrl(`/a/authz/api/v1/c/${companyId}/roles/${roleId}`, config.authzHost);
    },
  },
  slack: {
    v2: {
      searchChannelsUrl(pluginInstallId: string): string {
        return buildUrl(`/a/slack-plugin-v2/private/chat-bot/${pluginInstallId}/channels`, config.slackPluginHost);
      },
    },
  },
  teams: {
    v2: {
      searchChannelsUrl(pluginInstallId: string): string {
        return buildUrl(`/a/msteams-plugin-v2/private/chat-bot/${pluginInstallId}/channels`, config.teamsPluginHost);
      },
    },
  },
  analytics: {
    getAnalyticsSettingsUrl(companyId: string): string {
      return buildUrl(`/api/v1/c/${companyId}/analytics/settings`, config.officeAttendanceAnalyticsHost);
    },
    getCustomWorkbooksUrl(
      companyId: string,
      scope: WorkbookScopeType,
      page: number,
      limit: number,
      locationId?: string,
    ): string {
      return buildUrl(
        `/api/v1/c/${companyId}/workbooks?types=CUSTOM&sort=name-asc&scopes=${scope}${
          locationId ? `&locationId=${locationId}` : ''
        }&page=${page}&size=${limit}`,
        config.sigmaAnalyticsHost,
      );
    },
    getAllWorkbooksUrl(companyId: string, scope: WorkbookScopeType): string {
      return buildUrl(
        `/api/v1/c/${companyId}/workbooks?size=0&sort=name-asc&scopes=${scope}`,
        config.sigmaAnalyticsHost,
      );
    },
    getCreateWorkbookUrl(companyId: string): string {
      return buildUrl(`/api/v1/c/${companyId}/workbooks`, config.sigmaAnalyticsHost);
    },
    getSingleWorkbookUrl(companyId: string, workbookId: string): string {
      return buildUrl(`/api/v1/c/${companyId}/workbooks/${workbookId}`, config.sigmaAnalyticsHost);
    },
    getCompanySigmaUrl(companyId: string, workbookId: string): string {
      return buildUrl(`/api/v1/c/${companyId}/workbooks/${workbookId}/url/view`, config.sigmaAnalyticsHost);
    },
    getLocationSigmaUrl(companyId: string, locationId: string, workbookId: string): string {
      return buildUrl(
        `/api/v1/c/${companyId}/workbooks/${workbookId}/url/view?locationId=${locationId}`,
        config.sigmaAnalyticsHost,
      );
    },
    getEditCompanySigmaUrl(companyId: string, workbookId: string): string {
      return buildUrl(`/api/v1/c/${companyId}/workbooks/${workbookId}/url/edit`, config.sigmaAnalyticsHost);
    },
    getEditLocationSigmaUrl(companyId: string, workbookId: string, locationId: string): string {
      return buildUrl(
        `/api/v1/c/${companyId}/workbooks/${workbookId}/url/edit?locationId=${locationId}`,
        config.sigmaAnalyticsHost,
      );
    },
    scheduledExportsLocationUrl(loginRequired: boolean, companyId: string, locationId: string): string {
      const redirectUrl = encodeURIComponent(`/exports/location?company_id=${companyId}&location_id=${locationId}`);

      return buildUrl(
        `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
        config.workplaceAnalyticsHost,
      );
    },
    scheduledExportsGlobalUrl(loginRequired: boolean, companyId: string): string {
      const redirectUrl = encodeURIComponent(`/exports/global?company_id=${companyId}`);

      return buildUrl(
        `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
        config.workplaceAnalyticsHost,
      );
    },
    scheduledExportsModalLocationUrl(companyId: string, locationId: string, loginRequired: boolean): string {
      const redirectUrl = encodeURIComponent(`/exports/modal?company_id=${companyId}&location_id=${locationId}`);
      return buildUrl(
        `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
        config.workplaceAnalyticsHost,
      );
    },
    scheduledExportsModalGlobalUrl(companyId: string, loginRequired: boolean): string {
      const redirectUrl = encodeURIComponent(`/exports/modal?company_id=${companyId}`);
      return buildUrl(
        `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
        config.workplaceAnalyticsHost,
      );
    },
    settingsHrisUploadModalGlobalUrl(companyId: string, loginRequired: boolean): string {
      const redirectUrl = encodeURIComponent(`/settings/hrisUpload/modal?company_id=${companyId}`);
      return buildUrl(
        `/entrypoint?loginRequired=${loginRequired}&redirect_url=${redirectUrl}`,
        config.workplaceAnalyticsHost,
      );
    },
  },
  diplomatServer: {
    getDiplomatClientsUrl(locationId: string): string {
      return buildUrl(`/a/diplomat-server/clients?location_id=${locationId}`, config.diplomatServerHost);
    },
    saveDiplomatClientUrl(): string {
      return buildUrl(`/a/diplomat-server/clients`, config.diplomatServerHost);
    },
    deleteDiplomatClientUrl(id: string): string {
      return buildUrl(`/a/diplomat-server/clients/${id}`, config.diplomatServerHost);
    },
  },
  eventLogs: {
    search(companyId: number): string {
      return buildUrl(`/a/event-logs/api/v1/c/${companyId}/search`, config.apiOverwrittenHost);
    },
    facets(companyId: number): string {
      return buildUrl(`/a/event-logs/api/v1/c/${companyId}/facets`, config.apiOverwrittenHost);
    },
  },
  mapi: {
    pairUrl(): string {
      return buildUrl('/a/mapi/device-codes/pair');
    },
    devices: {
      devicesUrl(): string {
        return buildUrl(`/a/mapi/devices`);
      },
      deviceUrl(id: string): string {
        return buildUrl(`/a/mapi/devices/${id}`);
      },
      deviceConfigUrl(id: string): string {
        return buildUrl(`/a/mapi/devices/${id}/config`);
      },
    },
    maps: {
      regenerate(locationId: string): string {
        return buildUrl(`/a/mapi/maps/${locationId}?regenerate=true`);
      },
    },
    studio: {
      positionUrl(): string {
        return buildUrl(`/a/mapi/studio/position`);
      },
      publishUrl(id: string): string {
        return buildUrl(`/a/mapi/studio/${id}/publish`);
      },
      refreshUrl(id: string): string {
        return buildUrl(`/a/mapi/studio/${id}/refresh`);
      },
    },
  },
  rto: {
    settingsUrl(locationId: string): string {
      return buildUrl(`/a/rto/settings?locationId=${locationId}`);
    },
  },
};
